// Physical-geometry helpers shared by the mesh (what gets rendered) and the solvability
// checker (what a player can actually traverse). Keeping ONE source of truth for the curb
// set is the whole point: the checker treats exactly the curbs the mesh draws as walls, so
// a curb that lands on a walkable slope is caught instead of silently shipped.

import { DVEC, type CellInfo, type Tile } from "./grammar";
import { topY } from "../config";

export const edgeAxisOf = (dir: number): 0 | 1 => (dir === 1 || dir === 3 ? 1 : 0);

/** Top-corner heights [A(x0,z0), B(x1,z0), C(x1,z1), D(x0,z1)] of a ramp tile. */
export function rampCornerHeights(t: Tile): [number, number, number, number] {
  const back = topY(t.ramp!.backLevel);
  const front = topY(t.ramp!.frontLevel);
  switch (t.ramp!.travelDir) {
    case 0:
      return [back, back, front, front];
    case 2:
      return [front, front, back, back];
    case 1:
      return [back, front, front, back];
    default:
      return [front, back, back, front];
  }
}

/** Planar surface height of a ramp tile at world (x,z) — used by rendering and pathfinding.
 *  Allocation-free (this is called thousands of times/frame by the army's ground probes). */
export function rampSurfaceHeight(t: Tile, x: number, z: number): number {
  const back = topY(t.ramp!.backLevel);
  const front = topY(t.ramp!.frontLevel);
  let hA: number;
  let hB: number;
  let hC: number;
  let hD: number;
  switch (t.ramp!.travelDir) {
    case 0:
      hA = back; hB = back; hC = front; hD = front;
      break;
    case 2:
      hA = front; hB = front; hC = back; hD = back;
      break;
    case 1:
      hA = back; hB = front; hC = front; hD = back;
      break;
    default:
      hA = front; hB = back; hC = back; hD = front;
  }
  const u = (x - t.x0) / (t.x1 - t.x0);
  const v = (z - t.z0) / (t.z1 - t.z0);
  const top = hA + (hB - hA) * u;
  const bot = hD + (hC - hD) * u;
  return top + (bot - top) * v;
}

/** Can a player walk across the A|B cell boundary, ignoring curbs? True when the two top
 *  surfaces meet at the same level, or the boundary is a ramp END bridging one level along
 *  that ramp's travel axis. A ramp's sloped SIDES (perpendicular) are not crossable. */
export function stepBetween(a: CellInfo, b: CellInfo, edgeAxis: 0 | 1): boolean {
  if (a.level === b.level) return true;
  if (Math.abs(a.level - b.level) > 1) return false;
  if (a.kind === "ramp" && a.rampAxis === edgeAxis) return true;
  if (b.kind === "ramp" && b.rampAxis === edgeAxis) return true;
  return false;
}

/** Canonical key for the undirected boundary between a cell and its neighbor in `dir`. */
export function edgeKey(cx: number, cz: number, dir: number): string {
  const [dx, dz] = DVEC[dir];
  const a = `${cx},${cz}`;
  const b = `${cx + dx},${cz + dz}`;
  return a < b ? `${a}|${b}` : `${b}|${a}`;
}

export interface CurbPlacement {
  cx: number;
  cz: number;
  dir: number;
  level: number;
}
export interface CurbSet {
  /** Every curb to render. */
  placements: CurbPlacement[];
  /** Boundaries between two EXISTING cells that a curb walls off (blocks traversal).
   *  Perimeter curbs (neighbor is empty) are not here — they wall nothing walkable. */
  blocked: Set<string>;
}

/** The curbs the mesh renders AND the walls the solvability checker must respect. */
export function computeCurbs(cells: Map<string, CellInfo>): CurbSet {
  const placements: CurbPlacement[] = [];
  const blocked = new Set<string>();
  for (const [k, a] of cells) {
    if (a.kind === "ramp") continue; // ramp cells carry no curbs (a curb would float on the slope)
    const [cx, cz] = k.split(",").map(Number);
    for (let d = 0; d < 4; d++) {
      const [dx, dz] = DVEC[d];
      const b = cells.get(`${cx + dx},${cz + dz}`);
      if (!b) {
        placements.push({ cx, cz, dir: d, level: a.level }); // perimeter rail
        continue;
      }
      // Never rail a WALKABLE transition (same level, or a ramp end) — that would wall off a
      // slope the player needs. Curbs go only on genuine drops, railed from the higher side.
      if (stepBetween(a, b, edgeAxisOf(d))) continue;
      if (b.level < a.level) {
        placements.push({ cx, cz, dir: d, level: a.level });
        blocked.add(edgeKey(cx, cz, d));
      }
    }
  }
  return { placements, blocked };
}
